import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiCheck, FiMic, FiRefreshCw, FiSquare, FiVolume2 } from 'react-icons/fi';
import { useI18n } from '../i18n/I18nProvider';
import { beginAudioSession, disposeAudioSession, prefetchSessionAudio } from '../lib/sessionAudioCache';
import { pronunciationPassed } from '../lib/comprehensiveTraining';
import {
  confirmPronunciationCard,
  initializePronunciationBoard,
  pronunciationItemKey,
  pronunciationResultStatus,
  updatePronunciationCard,
} from '../lib/pronunciationBoard';
import { collectPronunciationWords, pronunciationTokenKey, tokenizePronunciationSentence } from '../lib/pronunciationTokens';

const PRONUNCIATION_WORD_TAP_KEY = 'lexilab.pronunciation.word-tap';

const preferredRecordingType = () => {
  if (!window.MediaRecorder) return '';
  return [
    'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4',
  ].find((type) => window.MediaRecorder.isTypeSupported?.(type)) || '';
};

const Card = ({ children, className = '' }) => (
  <section className={`border border-gray-200 bg-white rounded-lg shadow-sm ${className}`}>{children}</section>
);

const replaceOrRemoveCard = (board, key) => {
  const cardIndex = board.cards.findIndex((card) => card.key === key);
  if (cardIndex < 0) return board;
  const [replacement, ...remaining] = board.pending;
  if (!replacement) {
    return { cards: board.cards.filter((card) => card.key !== key), pending: remaining };
  }
  return {
    cards: board.cards.map((card, index) => (index === cardIndex
      ? { key: pronunciationItemKey(replacement), item: replacement, status: 'ready', hadFailure: false, feedback: null, error: '' }
      : card)),
    pending: remaining,
  };
};

const PronunciationBoard = ({
  items = [],
  onFinish,
  onExit,
  onBoardEmpty,
  onItemPassed,
  onItemFailed,
  onItemRetryPassed,
  onItemRelearn,
  allowFailedCardConfirmation = false,
  showHeader = true,
  showGuidance = true,
  playAudio = async () => {},
  stopAudio = () => {},
}) => {
  const { locale, t } = useI18n();
  const allItems = useMemo(() => (items || []).filter((item) => item?.pronunciation_sentence), [items]);
  const [board, setBoard] = useState(() => initializePronunciationBoard(allItems));
  const [recordingKey, setRecordingKey] = useState('');
  const [relearningKey, setRelearningKey] = useState('');
  const [passedCount, setPassedCount] = useState(0);
  const [audioProgress, setAudioProgress] = useState({ done: 0, total: Math.min(6, allItems.length), failed: 0 });
  const [wordAudioProgress, setWordAudioProgress] = useState({ done: 0, total: 0, failed: 0 });
  const [wordTapEnabled, setWordTapEnabled] = useState(() => {
    try {
      return localStorage.getItem(PRONUNCIATION_WORD_TAP_KEY) === '1';
    } catch (_) {
      return false;
    }
  });
  const attemptsByKeyRef = useRef(new Map());
  const attemptsRef = useRef([]);
  const passedRef = useRef(new Set());
  const mistakesRef = useRef(new Map());
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const busyRef = useRef(0);
  const startedRef = useRef(performance.now());
  const finishedRef = useRef(false);
  const emptyReportedRef = useRef(false);
  const mountedRef = useRef(true);
  const pronunciationWords = useMemo(() => collectPronunciationWords(allItems), [allItems]);
  const pronunciationWordMap = useMemo(() => new Map(
    pronunciationWords.map((word) => [pronunciationTokenKey(word), word]),
  ), [pronunciationWords]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const updateCard = useCallback((key, updates) => {
    setBoard((current) => ({
      ...current,
      cards: updatePronunciationCard(current.cards, key, updates),
    }));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    emptyReportedRef.current = false;
    const sessionKey = allItems.map(pronunciationItemKey).join('|');
    beginAudioSession(`pronunciation-three-card:${sessionKey}`);
    prefetchSessionAudio(
      allItems.slice(0, 6).map((item) => ({ text: item.pronunciation_sentence, mode: 'sentence' })),
      { concurrency: 3, onProgress: setAudioProgress },
    );

    let cancelled = false;
    let completedWords = 0;
    let failedWords = 0;
    const totalWords = pronunciationWords.length;
    const reportWordProgress = () => {
      if (!cancelled) setWordAudioProgress({ done: completedWords, total: totalWords, failed: failedWords });
    };
    const waitForAvailableAudioLane = async () => {
      while (!cancelled && busyRef.current > 0) {
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
    };
    const warmWords = async () => {
      const initialWords = pronunciationWords.slice(0, 5).map((word) => ({ text: word, mode: 'word' }));
      const remainingWords = pronunciationWords.slice(5).map((word) => ({ text: word, mode: 'word' }));
      if (initialWords.length) {
        const initialResult = await prefetchSessionAudio(initialWords, {
          concurrency: 5,
          onProgress: (progress) => {
            completedWords = progress.done;
            failedWords = progress.failed;
            reportWordProgress();
          },
        });
        completedWords = initialResult.done;
        failedWords = initialResult.failed;
        reportWordProgress();
      }
      for (const entry of remainingWords) {
        if (cancelled) return;
        await waitForAvailableAudioLane();
        if (cancelled) return;
        const result = await prefetchSessionAudio([entry], { concurrency: 1, priority: 'low' });
        completedWords += result.done;
        failedWords += result.failed;
        reportWordProgress();
      }
    };
    warmWords().catch(() => {});
    return () => {
      cancelled = true;
      mountedRef.current = false;
      recorderRef.current?.state === 'recording' && recorderRef.current.stop();
      stopStream();
      stopAudio();
      disposeAudioSession();
    };
  }, [allItems, pronunciationWords, stopAudio, stopStream]);

  useEffect(() => {
    const windowItems = [
      ...board.cards.map((card) => card.item),
      ...board.pending.slice(0, 3),
    ];
    const audioBusy = busyRef.current > 0;
    prefetchSessionAudio(windowItems.map((item) => ({
      text: item.pronunciation_sentence,
      mode: 'sentence',
    })), { concurrency: audioBusy ? 1 : 3, priority: audioBusy ? 'low' : undefined });
  }, [board.cards, board.pending]);

  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    const attempts = attemptsRef.current;
    const rounds = Math.max(1, ...attemptsByKeyRef.current.values());
    onFinish?.({
      mode: 'pronunciation',
      words: allItems.map((item) => ({
        word: item.word,
        sense_ids: [item.sense_id || 'primary'],
        display_label: item.display_label,
      })),
      duration_ms: performance.now() - startedRef.current,
      first_pass_accuracy: allItems.length
        ? attempts.filter((attempt) => attempt.attempt_number === 1 && attempt.passed).length / allItems.length
        : 0,
      completion_rate: allItems.length ? passedRef.current.size / allItems.length : 0,
      rounds,
      average_response_ms: attempts.length
        ? attempts.reduce((sum, attempt) => sum + attempt.response_ms, 0) / attempts.length
        : 0,
      mistakes: [...mistakesRef.current.values()].map((item) => ({
        word: item.word,
        sense_ids: [item.sense_id || 'primary'],
        display_label: item.display_label,
      })),
      attempts,
      max_streak: passedRef.current.size,
    });
  }, [allItems, onFinish]);

  useEffect(() => {
    if (!allItems.length || board.cards.length > 0 || board.pending.length > 0) return;
    if (emptyReportedRef.current) return;
    emptyReportedRef.current = true;
    if (passedRef.current.size === allItems.length) finish();
    onBoardEmpty?.({ passed: passedRef.current.size === allItems.length, passedCount: passedRef.current.size });
  }, [allItems.length, board.cards.length, board.pending.length, finish, onBoardEmpty]);

  const toggleWordTap = () => {
    const next = !wordTapEnabled;
    setWordTapEnabled(next);
    try {
      localStorage.setItem(PRONUNCIATION_WORD_TAP_KEY, next ? '1' : '0');
    } catch (_) {}
  };

  const playWordToken = (token) => {
    if (recordingKey || !token?.speakable) return;
    playAudio(pronunciationWordMap.get(token.key) || token.text, 'word');
  };

  const gradeBlob = async (item, blob, elapsedMs, cardHadFailure = false) => {
    const key = pronunciationItemKey(item);
    const attemptNumber = (attemptsByKeyRef.current.get(key) || 0) + 1;
    attemptsByKeyRef.current.set(key, attemptNumber);
    updateCard(key, { status: 'grading', error: '', feedback: null });
    try {
      const body = new FormData();
      body.append('audio', blob, `reading.${blob.type.includes('mp4') ? 'm4a' : 'webm'}`);
      body.append('word', item.word);
      body.append('sense_id', item.sense_id || 'primary');
      body.append('locale', locale);
      const response = await fetch('/api/student/self-check/pronunciation/grade', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || t('Your recording could not be scored right now'));
      const passed = pronunciationPassed(data);
      const retryPassed = passed && cardHadFailure;
      if (retryPassed && allowFailedCardConfirmation) {
        const saved = await onItemRetryPassed?.(item, data);
        if (saved === false) throw new Error(t('Your progress could not be saved. Please try again.'));
      } else if (passed) {
        const saved = await onItemPassed?.(item, data);
        if (saved === false) throw new Error(t('Your progress could not be saved. Please try again.'));
      } else {
        await onItemFailed?.(item, data);
      }
      const attempt = {
        word: item.word,
        sense_ids: [item.sense_id || 'primary'],
        correct: passed,
        passed,
        score: data.score,
        spelling_correct: true,
        meaning_correct: true,
        timed_out: false,
        round: attemptNumber,
        attempt_number: attemptNumber,
        response_ms: elapsedMs,
        pronunciation_recording_id: data.recording_id,
        pronunciation_feedback: data.feedback || data.summary || '',
      };
      attemptsRef.current.push(attempt);
      if (passed && !retryPassed) {
        passedRef.current.add(key);
        mistakesRef.current.delete(key);
        setPassedCount(passedRef.current.size);
      } else {
        mistakesRef.current.set(key, item);
      }
      if (mountedRef.current) updateCard(key, {
        status: pronunciationResultStatus({
          passed,
          hadFailure: cardHadFailure,
          requireRelearn: allowFailedCardConfirmation,
        }),
        hadFailure: cardHadFailure || !passed,
        feedback: { ...data, attemptNumber },
        error: '',
      });
    } catch (gradeError) {
      attemptsByKeyRef.current.set(key, Math.max(0, attemptNumber - 1));
      if (mountedRef.current) updateCard(key, { status: 'ready', feedback: null, error: gradeError.message });
    } finally {
      busyRef.current = Math.max(0, busyRef.current - 1);
    }
  };

  const startRecording = async (card) => {
    if (recordingKey || ['grading', 'passed', 'relearn_ready'].includes(card.status)) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      updateCard(card.key, { error: t('Recording is not supported in this browser.') });
      return;
    }
    try {
      stopAudio();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = preferredRecordingType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];
      const startedAt = performance.now();
      const recordedItem = card.item;
      const recordedAfterFailure = Boolean(card.hadFailure);
      recorder.ondataavailable = (event) => event.data?.size && chunksRef.current.push(event.data);
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        stopStream();
        if (mountedRef.current) {
          setRecordingKey('');
          gradeBlob(recordedItem, blob, performance.now() - startedAt, recordedAfterFailure);
        }
      };
      recorder.start();
      busyRef.current += 1;
      setRecordingKey(card.key);
      updateCard(card.key, { status: 'recording', feedback: null, error: '' });
    } catch (_) {
      stopStream();
      updateCard(card.key, { error: t('Microphone access is needed for pronunciation practice.') });
    }
  };

  const stopRecording = (key) => {
    if (recordingKey !== key) return;
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  const confirmPassed = (key) => {
    setBoard((current) => confirmPronunciationCard(current, key));
  };

  const resolveFailed = async (card) => {
    if (relearningKey) return;
    setRelearningKey(card.key);
    try {
      const saved = await onItemRelearn?.(card.item);
      if (saved === false) throw new Error(t('Your progress could not be saved. Please try again.'));
      setBoard((current) => replaceOrRemoveCard(current, card.key));
    } catch (error) {
      updateCard(card.key, { error: error.message || t('Your progress could not be saved. Please try again.') });
    } finally {
      setRelearningKey('');
    }
  };

  const renderSentence = (item) => {
    if (!wordTapEnabled) return item.pronunciation_sentence;
    return tokenizePronunciationSentence(item.pronunciation_sentence).map((token, tokenIndex) => (
      token.speakable ? (
        <button
          type="button"
          key={`${token.key}-${tokenIndex}`}
          onClick={() => playWordToken(token)}
          disabled={Boolean(recordingKey)}
          className="rounded-sm px-0.5 text-left text-indigo-800 underline decoration-indigo-200 decoration-2 underline-offset-4 hover:bg-indigo-50 hover:decoration-indigo-500 disabled:cursor-default disabled:opacity-60"
          title={t('Play this word')}
        ><span data-i18n-skip>{token.text}</span></button>
      ) : <React.Fragment key={`text-${tokenIndex}`}><span data-i18n-skip>{token.text}</span></React.Fragment>
    ));
  };

  if (!allItems.length) {
    return <Card className="mx-auto max-w-3xl p-7"><p>{t('No pronunciation sentences are ready for this set yet.')}</p>{onExit && <button onClick={onExit} className="mt-4 border px-4 py-2 rounded-md">{t('Back')}</button>}</Card>;
  }

  return (
    <Card className="mx-auto max-w-7xl overflow-hidden">
      {showHeader && (
        <div className="flex flex-wrap items-center justify-between gap-4 border-b px-5 py-4">
          {onExit ? <button onClick={onExit} className="text-gray-500 hover:text-gray-900" title={t('Back')}><FiArrowLeft /></button> : <span />}
          <div className="text-center">
            <div className="font-semibold text-gray-900">{t('Pronunciation Practice')}</div>
            <div className="mt-0.5 text-sm text-gray-500">{t('Passed')} {passedCount}/{allItems.length} / {t('Waiting')} {board.pending.length}</div>
          </div>
          <button type="button" onClick={toggleWordTap} aria-pressed={wordTapEnabled} className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${wordTapEnabled ? 'border-indigo-300 bg-indigo-50 text-indigo-800' : 'border-gray-200 bg-white text-gray-700'}`}>
            <FiVolume2 /> {t('Tap words to listen')}
          </button>
        </div>
      )}
      {!showHeader && <div className="flex justify-end border-b px-5 py-3"><button type="button" onClick={toggleWordTap} aria-pressed={wordTapEnabled} className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold text-gray-700"><FiVolume2 /> {t('Tap words to listen')}</button></div>}
      {audioProgress.total > 0 && audioProgress.done < audioProgress.total && <div className="h-1 bg-indigo-50"><div className="h-full bg-indigo-500 transition-[width]" style={{ width: `${(audioProgress.done / audioProgress.total) * 100}%` }} /></div>}
      <div className="p-5 sm:p-7">
        {wordAudioProgress.total > 0 && wordAudioProgress.done < wordAudioProgress.total && (
          <div className="mb-5 max-w-sm" aria-live="polite">
            <div className="flex items-center justify-between gap-3 text-xs text-gray-500"><span>{t('Preparing word audio')}</span><span className="tabular-nums">{wordAudioProgress.done}/{wordAudioProgress.total}</span></div>
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100"><div className="h-full bg-indigo-500 transition-[width]" style={{ width: `${(wordAudioProgress.done / wordAudioProgress.total) * 100}%` }} /></div>
          </div>
        )}
        {showGuidance && <p className="mb-5 text-sm text-gray-600">{t('Practice another card while one is being scored. Each sentence must pass on its own.')}</p>}
        <div className="grid gap-5 lg:grid-cols-3">
          {board.cards.map((card) => {
            const isRecording = recordingKey === card.key;
            const isPassed = card.status === 'passed';
            const isFailed = card.status === 'failed';
            const isRelearnReady = card.status === 'relearn_ready';
            const isGrading = card.status === 'grading';
            const borderTone = isPassed || isRelearnReady
              ? 'border-emerald-300 bg-emerald-50/60'
              : isFailed
                ? 'border-amber-300 bg-amber-50/50'
                : isRecording
                  ? 'border-rose-300 bg-rose-50/40'
                  : isGrading
                    ? 'border-indigo-300 bg-indigo-50/40'
                    : 'border-gray-200 bg-white';
            return (
              <section key={card.key} className={`flex min-h-[34rem] flex-col rounded-lg border p-5 transition-colors ${borderTone}`}>
                <div className="flex items-start justify-between gap-3"><div><div className="text-xs font-semibold uppercase text-indigo-600">{t('Target word')}</div><h2 className="mt-1 text-2xl font-bold" data-i18n-skip>{card.item.display_label || card.item.word}</h2></div><button type="button" onClick={() => playAudio(card.item.pronunciation_sentence, 'sentence')} disabled={Boolean(recordingKey)} className="shrink-0 rounded-md border bg-white p-2 text-indigo-700 disabled:opacity-40" title={t('Replay sentence')}><FiVolume2 /></button></div>
                <p className="mt-5 text-lg leading-8 text-gray-900" data-i18n-skip>{renderSentence(card.item)}</p>
                {wordTapEnabled && <p className="mt-2 text-xs text-indigo-700">{t('Tap any word in the sentence to hear it.')}</p>}
                <div className="mt-5">
                  {isGrading ? <div className="inline-flex items-center gap-2 rounded-md bg-indigo-100 px-4 py-3 font-semibold text-indigo-800"><FiRefreshCw className="animate-spin" /> {t('Scoring your reading...')}</div>
                    : isRecording ? <button onClick={() => stopRecording(card.key)} className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-3 font-semibold text-white"><FiSquare /> {t('Finish recording')}</button>
                      : isRelearnReady ? <button onClick={() => resolveFailed(card)} disabled={Boolean(relearningKey)} className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-3 font-semibold text-white disabled:opacity-50"><FiRefreshCw /> {t('Ready to re-learn!')}</button>
                        : isPassed ? <button onClick={() => confirmPassed(card.key)} className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-3 font-semibold text-white"><FiCheck /> {t('Confirm and continue')}</button>
                          : <button onClick={() => startRecording(card)} disabled={Boolean(recordingKey)} className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-3 font-semibold text-white disabled:opacity-40"><FiMic /> {t(isFailed ? 'Record again' : 'Start recording')}</button>}
                </div>
                {card.error && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{t(card.error)}</div>}
                {card.feedback && <div className={`mt-5 rounded-md border p-4 ${isPassed || isRelearnReady ? 'border-emerald-200 bg-white/80' : 'border-amber-200 bg-white/80'}`}><div className="flex items-center justify-between gap-3"><h3 className={`font-bold ${isPassed || isRelearnReady ? 'text-emerald-800' : 'text-amber-900'}`}>{t(isPassed || isRelearnReady ? 'Passed' : 'Try once more')}</h3><strong className="text-2xl tabular-nums">{card.feedback.score}/100</strong></div>{card.feedback.feedback && <p className="mt-3 text-sm text-gray-800" data-i18n-skip>{card.feedback.feedback}</p>}{card.feedback.issues?.length > 0 && <div className="mt-4 space-y-3 border-t border-amber-200 pt-4">{card.feedback.issues.map((issue, issueIndex) => <div key={`${issue.segment}-${issueIndex}`} className="text-sm" data-i18n-skip><div className="font-semibold text-gray-950">{issue.segment}</div><div className="mt-1 text-gray-700">{issue.problem}</div><div className="mt-1 text-indigo-800">{issue.correction}</div></div>)}</div>} </div>}
                {showGuidance && <div className="mt-auto pt-5 text-xs text-gray-500">{isPassed ? t('Confirm when you are ready for the next sentence.') : isGrading ? t('You can practise either of the other cards now.') : t('Listen once, then read the sentence aloud. A clear target word matters most.')}</div>}
              </section>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

export default PronunciationBoard;
